import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import * as Query from '../../../api/gql/query'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import { formatIsoDate } from '../../schedule/lib/format'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import type { ExpenseMutations } from '../hooks/useExpenseMutations'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import type { ExpenseAffordances } from '../lib/invoices'
import { formatMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostExpenseDetail } from '../types'
import ExpenseDialog from './ExpenseDialog'
import ExpenseSheetSkeleton from './ExpenseSheetSkeleton'

const NOT_FOUND = 'CostControl_error_expense_not_found'

interface Props {
  expenseId: string | null
  projectId: string
  tree: CostTree
  currency: string
  locale: string
  af: ExpenseAffordances
  canLinkDocuments: boolean
  mutations: ExpenseMutations
  /** The latest refusal of a reload of this expense (it may be gone), as the tab heard it. */
  lostMessage: string | null
  onClose: () => void
}

/** Ruling Q9: an expense's page is this side sheet — details, edit, delete, attachments, history. */
const ExpenseSheet: React.FC<Props> = ({ expenseId, projectId, tree, currency, locale, af, canLinkDocuments, mutations, lostMessage, onClose }) => {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { data, loading, error } = useQuery<{ costExpense: CostExpenseDetail }>(Query.COST_EXPENSE, {
    variables: { id: expenseId },
    skip: !expenseId,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  useEffect(() => { setEditing(false); setDeleting(false) }, [expenseId])
  const message = lostMessage ?? error?.message ?? null
  const detail = data?.costExpense
  const gone = !!message && isRecordGoneError(message, NOT_FOUND)
  const expense = detail && !gone && detail.expense.projectId === projectId ? detail.expense : null
  const money = (v: string) => formatMoney(v, { locale, currency })

  const remove = async () => {
    if (!expense) return
    const res = await mutations.remove(expense.id)
    setDeleting(false)
    if (res.ok) {
      toastr.success('', t('CostControl_expense_deleted'))
      onClose()
    }
  }

  const row = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-[9rem_1fr] gap-3 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{value}</dd>
    </div>
  )

  return (
    <Sheet open={!!expenseId} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('CostControl_expense_title')}</SheetTitle>
          {expense && <SheetDescription>{expense.description}</SheetDescription>}
        </SheetHeader>
        {!expense && loading && <ExpenseSheetSkeleton />}
        {!expense && !loading && (
          <p className="mt-4 text-sm text-muted-foreground">{gone || !message ? t(NOT_FOUND) : describeCostControlError(message, t)}</p>
        )}
        {expense && detail && (
          <div className="mt-4 space-y-4">
            {(af.edit || af.remove) && (
              <div className="flex gap-2">
                {af.edit && (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                    {t('Common_edit')}
                  </Button>
                )}
                {af.remove && (
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleting(true)}>
                    <Trash2 className="h-4 w-4" />
                    {t('Common_delete')}
                  </Button>
                )}
              </div>
            )}
            <dl className="divide-y">
              {row(t('CostControl_expense_field_item'), `${expense.path ? `${expense.path} / ` : ''}${expense.nodeCode ? `${expense.nodeCode} · ` : ''}${expense.nodeName}`)}
              {row(t('CostControl_expense_field_supplier'), expense.supplierName ?? '—')}
              {row(t('CostControl_expense_field_date'), formatIsoDate(t, expense.date))}
              {row(t('CostControl_expense_field_amount'), <strong className="tabular-nums">{money(expense.amount)}</strong>)}
              {row(t('CostControl_expense_field_created'), `${expense.createdByName ?? '—'} · ${new Date(expense.createdAt).toLocaleString(t('date_locale') || 'cs-CZ')}`)}
            </dl>
            <RecordAttachmentsCard
              projectId={projectId}
              attachments={detail.attachments}
              canAdd={af.addAttachment}
              canRemove={af.removeAttachment}
              canLinkDocuments={canLinkDocuments}
              onAdd={(source) => mutations.addAttachment(expense.id, source)}
              onRemove={(attachmentId) => mutations.removeAttachment(expense.id, attachmentId)}
            />
            <RecordHistoryCard projectId={projectId} entityType="EXPENSE" entityId={expense.id} currency={currency} locale={locale} />
            <ExpenseDialog
              open={editing && af.edit}
              mode="edit"
              expense={expense}
              tree={tree}
              currency={currency}
              onOpenChange={setEditing}
              onSubmit={async (input) => (await mutations.update(expense.id, input)).ok}
            />
            <ConfirmDialog
              open={deleting}
              title={t('CostControl_expense_delete_title')}
              description={t('CostControl_expense_delete_text')}
              confirmLabel={t('Common_delete')}
              variant="destructive"
              submitting={mutations.saving}
              onCancel={() => setDeleting(false)}
              onConfirm={remove}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
export default ExpenseSheet
